import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, ArrowLeft, Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useGoogleLogin } from '@react-oauth/google';

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const getErrorStr = (detail, fallback) => {
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(', ');
    if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
    return fallback;
  };

  // ── Standard email/password login ───────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setLoading(true);
    try {
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const res = await fetch(`${apiBase}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (res.status === 429) { 
        setErrorMsg('Too many login attempts! Please wait a minute.');
        return;
      }

      if (res.ok && data.success) {
        if (data.role === 'admin') {
          localStorage.setItem('admin_authenticated', 'true');
          localStorage.setItem('admin_email', data.email);
          localStorage.setItem('admin_name', data.name || '');
          localStorage.setItem('admin_phone', data.phone || '');
          localStorage.setItem('user_role', 'admin');
          showToast(t('login.welcomeAdmin', { email: data.email, defaultValue: `Welcome back, Admin ${data.email}.` }));
          setTimeout(() => navigate(location.state?.from || '/admin'), 800);
        } else {
          localStorage.setItem('user_authenticated', 'true');
          localStorage.setItem('user_email', data.email);
          localStorage.setItem('user_name', data.name || '');
          localStorage.setItem('user_phone', data.phone || '');
          localStorage.setItem('user_role', 'user');
          showToast(t('login.welcomeUser', { email: data.email, defaultValue: `Welcome back, ${data.email}.` }));
          setTimeout(() => navigate(location.state?.from || '/'), 800);
        }
      } else {
        setErrorMsg(getErrorStr(data.detail, t('login.errInvalid', { defaultValue: 'Invalid credentials.' })));
      }
    } catch {
      setErrorMsg(t('login.errOffline', { defaultValue: 'Authentication server is currently offline.' }));
    } finally {
      setLoading(false);
    }
  };

  // ── Google OAuth login ───────────────────────────────────────────────────
  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true);
      try {
        // Fetch Google user profile using the access token
        const profileRes = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
        });
        const profile = await profileRes.json();

        // Register or login the user in the backend database
        const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const dbRes = await fetch(`${apiBase}/api/auth/google`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: profile.email,
            name: profile.name,
            avatar: profile.picture
          })
        });
        const dbData = await dbRes.json();

        if (dbRes.status === 429) { 
          setErrorMsg('Too many login attempts! Please wait a minute.');
          return;
        }


        if (dbRes.ok && dbData.success) {
          if (dbData.role === 'admin') {
            localStorage.setItem('admin_authenticated', 'true');
            localStorage.setItem('admin_email', dbData.email);
            localStorage.setItem('admin_name', dbData.name || '');
            localStorage.setItem('admin_phone', dbData.phone || '');
            localStorage.setItem('user_role', 'admin');
          } else {
            localStorage.setItem('user_authenticated', 'true');
            localStorage.setItem('user_email', dbData.email);
            localStorage.setItem('user_name', dbData.name || '');
            localStorage.setItem('user_phone', dbData.phone || '');
            localStorage.setItem('user_role', 'user');
          }
          localStorage.setItem('user_avatar', profile.picture || '');

          showToast(t('login.welcomeGoogle', { name: dbData.name || dbData.email, defaultValue: `Welcome, ${dbData.name || dbData.email}! 🙏` }));
          setTimeout(() => navigate(location.state?.from || (dbData.role === 'admin' ? '/admin' : '/')), 800);
        } else {
          showToast(getErrorStr(dbData.detail, t('login.errGoogleFail', { defaultValue: 'Google authentication failed on server.' })), 'error');
        }
      } catch (err) {
        alert('Google API Error: ' + err.message);
        showToast('Google sign-in failed. Please try again.', 'error');
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: (err) => {
      alert('Google Popup Error: ' + (err?.error_description || err?.error || JSON.stringify(err) || 'Unknown error'));
      showToast(t('login.errGoogleCancel', { defaultValue: 'Google sign-in was cancelled or failed.' }), 'error');
    }
  });

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        backgroundImage: 'linear-gradient(rgba(13, 13, 13, 0.4), rgba(26, 21, 8, 0.7)), url(/images/portal_bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        position: 'relative'
      }}
    >
      {/* Background Blur Overlay */}
      <div style={{ position: 'absolute', inset: 0, backdropFilter: 'blur(8px)', zIndex: 0 }} />

      {/* Toast */}
      {toast && (
        <div
          className="animate-fade-in auth-toast"
          style={{
            position: 'fixed',
            top: '90px',
            right: '30px',
            zIndex: 9999,
            backgroundColor: toast.type === 'error' ? 'var(--accent-terracotta)' : 'var(--accent-green)',
            color: '#fff',
            padding: '14px 24px',
            borderRadius: '8px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '0.85rem',
            fontWeight: '600'
          }}
        >
          {toast.type === 'error' ? <X size={18} /> : <Check size={18} />}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Back link */}
      <Link
        to="/"
        style={{
          position: 'absolute',
          top: '30px',
          left: '5%',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: 'var(--text-light)',
          textDecoration: 'none',
          fontSize: '0.85rem',
          fontWeight: '600',
          transition: 'var(--transition-fast)',
          zIndex: 1
        }}
        className="hover-gold"
      >
        <ArrowLeft size={16} /> {t('login.backHome', { defaultValue: 'Back to Home' })}
      </Link>

      {/* Card */}
      <div className="glass-card portal-card animate-fade-in" style={{ position: 'relative', zIndex: 1 }}>
        {/* Logo and Company Name */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '15px' }}>
          <div
            style={{
              width: '75px',
              height: '75px',
              borderRadius: '50%',
              background: '#ffffff',
              border: '2px solid rgba(214,179,106, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
              overflow: 'hidden',
              marginBottom: '12px'
            }}
          >
            <img src="/images/new-logo.png" alt="Mystic Routes Logo" style={{ width: '55px', height: '55px', objectFit: 'contain' }} />
          </div>
          <h1 style={{ fontSize: '1.2rem', color: 'var(--primary-gold)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 600, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>Mystic Routes</h1>
        </div>

        <h2 className="font-serif" style={{ fontSize: '1.8rem', color: 'var(--text-light)', marginBottom: '5px' }}>
          {t('login.title', { defaultValue: 'Portal Sign In' })}
        </h2>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '15px' }}>
          {t('login.subtitle', { defaultValue: 'Sign in to your Mystic Routes account' })}
        </p>

        {/* ── Google Button ── */}
        <button
          onClick={() => handleGoogleLogin()}
          disabled={googleLoading}
          className="google-btn"
        >
          {/* Official Google "G" SVG */}
          <svg width="18" height="18" viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            <path fill="none" d="M0 0h48v48H0z"/>
          </svg>
          {googleLoading ? t('login.btnGoogleLoading', { defaultValue: 'Signing in...' }) : t('login.btnGoogle', { defaultValue: 'Continue with Google' })}
        </button>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '15px' }}>
          <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(181,137,37,0.15)' }} />
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
            {t('login.orEmail', { defaultValue: 'or sign in with email' })}
          </span>
          <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(181,137,37,0.15)' }} />
        </div>

        {/* ── Email / Password Form ── */}
        <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div className="portal-input-container">
            <span className="portal-input-icon">
              <Mail size={18} />
            </span>
            <input
              type="email"
              placeholder={t("login.phEmail", { defaultValue: "Email Address" })}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="portal-input"
            />
          </div>

          <div className="portal-input-container">
            <span className="portal-input-icon">
              <Lock size={18} />
            </span>
            <input
              type="password"
              placeholder={t("login.phPassword", { defaultValue: "Password" })}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="portal-input"
            />
          </div>

          {errorMsg && (
            <p style={{ color: 'var(--accent-terracotta)', fontSize: '0.8rem', fontWeight: '500', margin: '0' }}>
              {errorMsg}
            </p>
          )}

          <button
            type="submit"
            className="btn-gold pulse-gold-button"
            disabled={loading}
            style={{
              padding: '12px',
              borderRadius: '30px',
              fontSize: '0.85rem',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              marginTop: '6px'
            }}
          >
            {loading ? t('login.btnVerifying', { defaultValue: 'Verifying...' }) : t('login.btnSignIn', { defaultValue: 'Sign In' })}
          </button>
        </form>

        {/* Register link */}
        <div style={{ marginTop: '15px', borderTop: '1px solid rgba(181,137,37,0.1)', paddingTop: '15px' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            {t('login.needAccount', { defaultValue: 'Need an account?' })}{' '}
            <Link to="/signup" style={{ color: 'var(--primary-gold)', fontWeight: '600', textDecoration: 'none' }}>
              {t('login.btnRegister', { defaultValue: 'Register Here' })}
            </Link>
          </span>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .auth-toast { top: auto !important; bottom: 20px !important; right: 16px !important; left: 16px !important; width: auto !important; }
        }
      `}</style>
    </div>
  );
}
